import Popover from "@/component/SimplePopover";
import SpeakerPopover from "./SpeakerPopover";
import { IPG } from "@/types/summary";
import { useEffect, useMemo, useRef, useState } from "react";
import { speakerNameLimitPattern } from "@/constants/record";
import fridayReporter from "@/utils/friday";
import InputWithClear from "../InputWithClear";

interface SpeakPeopleProps {
  record: IPG;
  allowEdit?: boolean;
  isGenerating?: boolean;
  handleEditSpeaker?: (name: string) => void;
}

const SpeakPeople: React.FC<SpeakPeopleProps> = (props) => {
  const { record, allowEdit = true, isGenerating = false, handleEditSpeaker } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [speakerPopoverVisible, setSpeakerPopoverVisible] = useState(false);
  const [inputValue, setInputValue] = useState(record.speakerName?.join(",") || "发言人");
  const originInput = useMemo(() => record.speakerName?.join(",") || "发言人", [record]);

  useEffect(() => {
    if (isEditing) return;
    const speakContent = record.sc
      ?.flatMap((scItem) => scItem.words)
      .map((wordItem) => wordItem.word)
      .join("");
    if (isGenerating && !speakContent?.length) {
      setInputValue("发言人");
    } else {
      setInputValue(record.speakerName?.join(",") || "发言人");
    }
  }, [record.speakerName, record.sc, isGenerating, isEditing]);

  const inputRef = useRef<HTMLInputElement>(null);
  const tempClickStatus = useRef(false);

  const handleEditSingle = (name: string) => {
    fridayReporter.pushData({
      eventCode: 'RecordPage_EditSpeaker_Choose'
    })
    handleCommit(name);
    inputRef.current?.focus();
  };

  const handleCommit = (name: string) => {
    if (!name.trim().length || name === originInput) {
      return;
    }
    setInputValue(name);
    setIsEditing(false);
    handleEditSpeaker?.(name);
    setTimeout(() => {
      setSpeakerPopoverVisible(false);
    }, 100);
  };

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
      setSpeakerPopoverVisible(true);
    }
  }, [isEditing]);

  return (
    <div className="mr-2">
      <Popover
        onVisibleChange={(visible) => {
          if (allowEdit) {
            if (!visible && tempClickStatus.current) {
              tempClickStatus.current = false;
              return;
            }
            if (!visible) {
              setInputValue(originInput);
              setIsEditing(false);
            }
            setSpeakerPopoverVisible(visible);
          }
        }}
        visible={speakerPopoverVisible}
        content={<SpeakerPopover value={inputValue} onChange={handleEditSingle} />}
      >
        {isEditing ? (
          <InputWithClear
            ref={inputRef}
            value={inputValue}
            onMyChange={(value) => setInputValue(value)}
            onClickCapture={() => {
              tempClickStatus.current = true;
            }}
            autoFocus
            limitPattern={speakerNameLimitPattern}
            maxLength={10}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleCommit(inputValue);
              }
            }}
            onBlur={()=>{
              handleCommit(inputValue);
            }}
            className="px-1 py-0.875 h-6 color-#626773 text-3.5 font-400 border-#4981F2 border-solid border-1 bg-transparent outline-none rounded-1"
          />
        ) : (
          <div
            className={`rounded-1 color-#626773 text-3.5 font-400 ${allowEdit ? "cursor-text" : ""}`}
            onClick={() => {
              if (allowEdit) {
                fridayReporter.pushData({
                  eventCode: 'RecordPage_EditSpeaker'
                })
                setIsEditing(true);
                setSpeakerPopoverVisible(true);
                tempClickStatus.current = true;
              }
            }}
          >
            {inputValue}
          </div>
        )}
      </Popover>
    </div>
  );
};

export default SpeakPeople;
